import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../config.js';
import { FaSearch, FaCalendarAlt, FaUser } from 'react-icons/fa';
import './EFT.css';

const EFT = () => {
  const [eftList, setEftList] = useState([]);
  const [filteredEftList, setFilteredEftList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  
  // Pagination state'leri
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchEFTList();
  }, []);

  const fetchEFTList = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');
      
      // Yeni EFT endpoint'ini kullan
      const response = await axios.get(`${getApiUrl()}/eft`, {
        headers: {
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
          'Content-Type': 'application/json',
        },
      });
      console.log(response);
      if (response.data.success) {
        setEftList(response.data.data);
        setFilteredEftList(response.data.data);
        setError(null);
      } else {
        setError(response.data.error || 'EFT listesi alınamadı');
        setEftList([]);
      }
    } catch (err) {
      console.error('EFT listesi çekilirken hata:', err);
      if (err.response?.status === 403) {
        setError('Bu işlem için yönetici yetkisi gereklidir');
      } else if (err.response?.status === 401) {
        setError('Oturum süreniz dolmuş, lütfen tekrar giriş yapın');
      } else {
        setError('EFT listesi yüklenirken bir hata oluştu');
      }
      setEftList([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    if (!amount) return '0,00 ₺';
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  // Filtreleme fonksiyonu
  const filterEFTList = () => {
    let filtered = eftList;

    // İsme göre filtreleme
    if (searchTerm) {
      filtered = filtered.filter(order => 
        order.ad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.soyad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Tarihe göre filtreleme
    if (dateFilter) {
      const filterDate = new Date(dateFilter);
      filtered = filtered.filter(order => {
        if (!order.siparis_tarihi) return false;
        const orderDate = new Date(order.siparis_tarihi);
        return orderDate.toDateString() === filterDate.toDateString();
      });
    }

    setFilteredEftList(filtered);
    setCurrentPage(1); // Filtreleme yapıldığında ilk sayfaya dön
  };

  // Search term veya date filter değiştiğinde filtreleme yap
  useEffect(() => {
    filterEFTList();
  }, [searchTerm, dateFilter, eftList]);

  // Pagination hesaplamaları
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEftList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEftList.length / itemsPerPage);

  // Sayfa değiştirme fonksiyonu
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Sayfa değiştiğinde en üste scroll
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Sayfa numaralarını oluştur
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Tüm sayfaları göster
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Akıllı sayfa numarası gösterimi
      if (currentPage <= 3) {
        // İlk sayfalardayız
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Son sayfalardayız
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        // Ortada bir yerdeyiz
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  if (loading) {
    return (
      <div className="eft-container">
        <div className="eft-loading">
          <div className="spinner"></div>
          <p>EFT listesi yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="eft-container">
        <div className="eft-error">
          <h3>Hata</h3>
          <p>{error}</p>
          <button onClick={fetchEFTList} className="retry-button">
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="eft-container">
      <div className="eft-header">
        <h1>EFT Ödemelerim</h1>
      </div>

      {/* Search ve Filtreleme Bölümü */}
      <div className="eft-search-section">
        <div className="search-filters">
          <div className="search-input-group">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="İsim, soyisim veya email ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="date-filter-group">
            <FaCalendarAlt className="date-icon" />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="date-input"
            />
          </div>
          
          <button 
            onClick={() => {
              setSearchTerm('');
              setDateFilter('');
            }}
            className="clear-filters-btn"
          >
            Filtreleri Temizle
          </button>
        </div>
        
        {searchTerm || dateFilter ? (
          <div className="active-filters">
            <span className="filter-label">Aktif Filtreler:</span>
            {searchTerm && (
              <span className="filter-tag">
                <FaUser className="filter-icon" />
                Arama: {searchTerm}
                <button 
                  onClick={() => setSearchTerm('')}
                  className="remove-filter-btn"
                >
                  ×
                </button>
              </span>
            )}
            {dateFilter && (
              <span className="filter-tag">
                <FaCalendarAlt className="filter-icon" />
                Tarih: {new Date(dateFilter).toLocaleDateString('tr-TR')}
                <button 
                  onClick={() => setDateFilter('')}
                  className="remove-filter-btn"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        ) : null}
      </div>

      {currentItems.length === 0 ? (
        <div className="eft-empty">
          {eftList.length === 0 ? (
            <>
              <p>Henüz EFT ile ödeme yapılan sipariş bulunmamaktadır.</p>
              <p className="eft-empty-subtitle">EFT ödemeli siparişler burada listelenecektir.</p>
            </>
          ) : (
            <>
              <p>Arama kriterlerinize uygun EFT ödemesi bulunamadı.</p>
              <p className="eft-empty-subtitle">Farklı arama terimleri deneyin veya filtreleri temizleyin.</p>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="eft-table-container">
            <table className="eft-table">
              <thead>
                <tr>
                  <th>Sipariş No</th>
                  <th>Müşteri</th>
                  <th>Tutar</th>
                  <th>Ürün Sayısı</th>
                  <th>Sipariş Tarihi</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((eft, index) => (
                  <tr key={eft.id || index} className="eft-row">
                    <td className="order-id">#{eft.id}</td>
                    <td className="customer-info">
                      <div className="customer-name">{eft.ad} {eft.soyad}</div>
                      <div className="customer-email">{eft.email || '-'}</div>
                    </td>
                    <td className="amount">{formatAmount(eft.toplam_tutar)}</td>
                    <td className="product-count">{eft.toplam_urun_sayisi || 0}</td>
                    <td>{formatDate(eft.siparis_tarihi)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="eft-pagination">
              <div className="pagination-info">
                <span>
                  {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredEftList.length)} / {filteredEftList.length} kayıt
                </span>
              </div>
              
              <div className="pagination-controls">
                {/* Önceki sayfa */}
                <button
                  className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  ← Önceki
                </button>
                
                {/* Sayfa numaraları */}
                <div className="page-numbers">
                  {getPageNumbers().map((pageNumber, index) => (
                    <button
                      key={index}
                      className={`page-number ${pageNumber === currentPage ? 'active' : ''} ${pageNumber === '...' ? 'ellipsis' : ''}`}
                      onClick={() => typeof pageNumber === 'number' ? handlePageChange(pageNumber) : null}
                      disabled={pageNumber === '...'}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>
                
                {/* Sonraki sayfa */}
                <button
                  className={`pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Sonraki →
                </button>
              </div>
              
              {/* Sayfa başına kayıt sayısı seçimi */}
              <div className="items-per-page">
                <label htmlFor="itemsPerPageSelect">Sayfa başına:</label>
                <select
                  id="itemsPerPageSelect"
                  value={itemsPerPage}
                  onChange={(e) => {
                    const newItemsPerPage = parseInt(e.target.value);
                    setCurrentPage(1);
                    // itemsPerPage state'ini güncellemek için bir callback kullan
                    // Bu örnekte sabit 10 olarak bırakıyoruz
                  }}
                  className="items-per-page-select"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EFT;
